import { ref, computed, triggerRef, watch } from 'vue';
import dataService from '../../services/dataService.js';
import { getNextAppointment } from './useAppointments.js';
import { getCurrentUser } from '../../firebase/auth.js';

// Shared state (singleton pattern - state is shared across all component instances)
const patients = ref([]);
const appointments = ref([]);
const results = ref([]);
const doses = ref([]);
const drugs = ref([]);
const notes = ref([]);
// Patients whose notes have been fetched (even if the result was empty), and
// in-flight note fetches keyed by patientId. Both back the note cache that lets
// NoteButton resolve locally instead of issuing one /notes/find per element.
const notesLoadedPatientIds = new Set();
const notesFetchesInFlight = new Map();
const singleDoses = ref([]);
const isLoading = ref(false);
const isEnriched = ref(false);
const lastFetched = ref(null);
const isCreatingNewPatient = ref(false); // Track if user is creating a new patient
const lastUserId = ref(null); // Track which user's data is cached

// Selected-patient state. These are backed by reactive refs (module-scope so all
// components share one source of truth) that mirror localStorage. Previously they
// were computeds whose getters read localStorage directly with NO reactive
// dependency, so Vue cached the first value and the setter's writes never
// invalidated it — selectedPatient could silently point at a stale/other patient
// (a safety hazard in a clinical app). Always mutate via setSelectedPatientId /
// setActiveSelectedPatientId so the ref and localStorage stay in sync.
const SELECTED_PATIENT_KEY = 'inr_selected_patient_id';
const ACTIVE_SELECTED_PATIENT_KEY = 'inr_active_selected_patient_id';

const readStoredId = (key) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const selectedPatientIdRef = ref(readStoredId(SELECTED_PATIENT_KEY));
const activeSelectedPatientIdRef = ref(readStoredId(ACTIVE_SELECTED_PATIENT_KEY));

const persistSelectedId = (key, refObj, value) => {
  const normalized = value ?? null;
  refObj.value = normalized;
  if (normalized !== null && normalized !== undefined) {
    localStorage.setItem(key, JSON.stringify(normalized));
  } else {
    localStorage.removeItem(key);
  }
};

// Concurrency control for fetchPatients:
// - inFlightFetch holds the currently-running fetch promise so concurrent
//   callers share a single request instead of racing.
// - fetchToken is a monotonically increasing request id; only the newest
//   request is allowed to commit its results to the shared reactive state,
//   so a slow older response can never clobber fresh data.
let inFlightFetch = null;
let fetchToken = 0;

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

/**
 * Shared composable for patient data management across the app
 * Uses singleton pattern - all components share the same state
 */
export function usePatientStore() {

  // Computed: Map of drug ID to drug name
  const drugsById = computed(() => {
    return drugs.value.reduce((acc, drug) => {
      acc[drug.id] = drug;
      return acc;
    }, {});
  });

  // nextAppointment used to be a snapshot baked onto the patient object at
  // enrichment time, refreshed only when every appointment-writing call site
  // remembered to call reEnrichPatient() afterward — some didn't, so the
  // calendar (reads appointments.value live) and this cached field could show
  // two different dates for the same patient. Deriving it here instead, live
  // off appointments.value, removes the cache entirely so there is nothing left
  // to go stale. Mirrors reEnrichPatient's field priority (appointmentDate
  // falling back to testDate) and its "strictly after today" cutoff.
  const getLiveNextAppointmentDate = (patientId) => {
    const numericPatientId = parseInt(patientId);
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const futureDates = appointments.value
      .filter(apt => parseInt(apt.patientId) === numericPatientId)
      .map(apt => String(apt.appointmentDate || apt.testDate || '').slice(0, 10))
      .filter(dateStr => dateStr > todayStr)
      .sort();
    return futureDates[0] || null;
  };

  const withLiveNextAppointment = (patientList) => patientList.map(p => {
    const nextAppointment = getLiveNextAppointmentDate(p.id);
    return nextAppointment === p.nextAppointment ? p : { ...p, nextAppointment };
  });

  // Computed: every patient, with a live (never-cached) nextAppointment. This is
  // what gets exposed as `patients` below, and everything else in this store
  // that lists/looks up patients reads from it too, so there is a single place
  // where the value is derived instead of N call sites that must remember to
  // refresh a cache. Writable so `patients.value = [...]` (fetchPatients, tests)
  // still replaces the underlying data as before — only reads are overlaid.
  const livePatients = computed({
    get: () => withLiveNextAppointment(patients.value),
    set: (value) => { patients.value = value; },
  });

  // Computed: Only active patients (isActive !== false, handles undefined/null as active)
  const activePatients = computed(() => {
    return livePatients.value.filter(p => p.isActive !== false && p.isActive !== 0);
  });

  // Selected patient ID (for patients management section - can include inactive).
  // Reactive-ref backed; writes go through the ref + localStorage in one step.
  const selectedPatientId = computed({
    get: () => selectedPatientIdRef.value,
    set: (value) => persistSelectedId(SELECTED_PATIENT_KEY, selectedPatientIdRef, value)
  });

  // Active selected patient ID (for other sections - only active patients).
  const activeSelectedPatientId = computed({
    get: () => activeSelectedPatientIdRef.value,
    set: (value) => persistSelectedId(ACTIVE_SELECTED_PATIENT_KEY, activeSelectedPatientIdRef, value)
  });

  // Computed: Get selected patient object
  const selectedPatient = computed(() => {
    if (!selectedPatientId.value) return null;
    return livePatients.value.find(p => p.id === selectedPatientId.value) || null;
  });

  // Computed: Get active selected patient object (for non-patients sections)
  const activeSelectedPatient = computed(() => {
    if (!activeSelectedPatientId.value) return null;
    return activePatients.value.find(p => p.id === activeSelectedPatientId.value) || null;
  });

  /**
   * Sync activeSelectedPatientId - if current active selection is inactive, select first active patient
   */
  const syncActiveSelectedPatient = () => {
    const currentActiveId = activeSelectedPatientId.value;
    const currentPatient = patients.value.find(p => p.id === currentActiveId);
    
    // If no current selection, or current patient is inactive, select first active patient
    if (!currentPatient || currentPatient.isActive === false || currentPatient.isActive === 0) {
      const firstActivePatient = activePatients.value[0];
      if (firstActivePatient) {
        activeSelectedPatientId.value = firstActivePatient.id;
      } else {
        activeSelectedPatientId.value = null;
      }
    }
  };

  /**
   * Check if cache is still valid
   */
  const isCacheValid = () => {
    if (!lastFetched.value) return false;
    const now = Date.now();
    return (now - lastFetched.value) < CACHE_DURATION;
  };

  /**
   * Fetch all patient data from the backend with full enrichment
   * @param {boolean} forceRefresh - Force refresh even if cache is valid
   */
  const fetchPatients = async (forceRefresh = false) => {

    // Check if user has changed - if so, invalidate cache
    const currentUser = getCurrentUser();
    const currentUserId = currentUser?.uid || null;

    if (currentUserId && lastUserId.value && currentUserId !== lastUserId.value) {
      clearCache();
      forceRefresh = true;
    }

    // Update tracked user ID
    if (currentUserId) {
      lastUserId.value = currentUserId;
    }

    // Skip if cache is valid and not forcing refresh
    if (!forceRefresh && isCacheValid() && isEnriched.value) {
      console.log('⏭️ fetchPatients: Using cached data, not refetching');
      return patients.value;
    }

    // In-flight guard: if a fetch is already running, return the existing
    // promise so concurrent callers share one request instead of racing.
    // A forceRefresh still supersedes (a new token is issued below) so the
    // most recent intent wins, but we only coalesce when not forcing.
    if (inFlightFetch && !forceRefresh) {
      return inFlightFetch;
    }

    const runFetch = doFetchPatients(forceRefresh);
    inFlightFetch = runFetch;
    try {
      return await runFetch;
    } finally {
      // Only clear the in-flight handle if it's still ours (a newer forced
      // fetch may have replaced it).
      if (inFlightFetch === runFetch) {
        inFlightFetch = null;
      }
    }
  };

  /**
   * Internal implementation of the patient fetch + enrichment pipeline.
   * Uses a request token so only the newest fetch commits to shared state.
   */
  const doFetchPatients = async (forceRefresh) => {
    // Claim a request token; only the request holding the latest token is
    // allowed to write results into the shared reactive state.
    const myToken = ++fetchToken;
    const isStale = () => myToken !== fetchToken;

    console.log('🔄 fetchPatients: Refetching from server (forceRefresh:', forceRefresh, ', cacheValid:', isCacheValid(), ', isEnriched:', isEnriched.value, ')');

    isLoading.value = true;
    isEnriched.value = false;  // Mark as not enriched when fetching

    try {
      // Step 1: Fetch base patient data
      const response = await dataService.getAll({ 
        selectTable: 'patients',
        id: 'patientsDetails'
      });
      
      if (!response?.data || response.data.length === 0) {
        // A newer fetch superseded us - don't clobber its (possibly fresher) state.
        if (isStale()) {
          return patients.value;
        }
        patients.value = [];
        lastFetched.value = Date.now();
        isEnriched.value = true;
        return patients.value;
      }

      // Step 2: Fetch shared data needed for enrichment (but NOT patients again!)
      await Promise.all([
        // Skip fetchAppointments - we'll collect them per-patient
        fetchResults(forceRefresh),
        fetchDrugs(forceRefresh),
      ]);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Collect all appointments from all patients
      const allAppointments = [];
      // Collect all recurrent doses locally; we assign to the shared reactive
      // `doses` array once at the end (no incremental mid-flight mutation).
      const allDoses = [];

      // Step 3: Enrich each patient with additional data
      const enrichedPatients = await Promise.all(
        response.data.map(async (patient) => {
          try {
            // Fetch drug targets for this patient
            const drugTargetsResponse = await dataService.getAll({
              selectTable: 'drugs_by_patients',
              id: 'patientsDetails',
              payload: { criteria: { id: patient.id } },
            });
            const drugTargets = drugTargetsResponse.data || [];

            // Fetch appointments for this patient
            const appointmentsResponse = await dataService.getAll({
              selectTable: 'test_schedules',
              id: 'patientsDetails',
              payload: { criteria: { id: patient.id } },
            });
            const patientAppointments = appointmentsResponse.data || [];
            
            // Add to global appointments collection with appointmentDate field
            // Using a Set to track unique appointment IDs and prevent duplicates
            patientAppointments.forEach(apt => {
              // Skip if we already have this appointment
              if (allAppointments.some(a => a.id === apt.id)) {
                return;
              }
              
              let appointmentDate = null;
              
              if (apt.testDate) {
                const testDateStr = String(apt.testDate);
                
                // Extract date without timezone conversion
                // testDate can be in formats like:
                // - "YYYY-MM-DD HH:MM:SS"
                // - "DD-MM-YYYY HH:MM:SS"
                // - ISO string
                
                if (testDateStr.includes(' ')) {
                  // Format: "YYYY-MM-DD HH:MM:SS" or "DD-MM-YYYY HH:MM:SS"
                  const datePart = testDateStr.split(' ')[0];
                  
                  if (datePart.includes('-')) {
                    const parts = datePart.split('-');
                    
                    // Check if it's DD-MM-YYYY or YYYY-MM-DD
                    if (parts[0].length === 4) {
                      // Already YYYY-MM-DD
                      appointmentDate = datePart;
                    } else {
                      // DD-MM-YYYY -> YYYY-MM-DD
                      appointmentDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
                    }
                  }
                } else if (testDateStr.length >= 10) {
                  // Just take the first 10 characters (YYYY-MM-DD)
                  appointmentDate = testDateStr.slice(0, 10);
                }
              }
              
              allAppointments.push({
                ...apt,
                appointmentDate
              });
            });

            // Get results for this patient from cache
            const patientResults = results.value.filter(
              (result) => result.patientId === patient.id
            );

            // Fetch recurrent doses for this patient's results and add to store
            const recurrentDoses = [];
            if (patientResults.length > 0) {
              const dosesResponse = await dataService.getAll({
                selectTable: 'daily_doses',
                id: 'patientsDetails',
                payload: { criteria: { patientId: patient.id } },
              });
              const fetchedDoses = dosesResponse.data || [];
              recurrentDoses.push(...fetchedDoses);

              // Accumulate into local collector (avoid duplicates); committed
              // to the shared `doses` ref once at the end of enrichment.
              fetchedDoses.forEach(dose => {
                if (!allDoses.find(d => d.id === dose.id)) {
                  allDoses.push(dose);
                }
              });
            }

            // Create resultsWithDoses by combining results with their doses
            const resultsWithDoses = patientResults.map((result) => {
              const resultDoses = recurrentDoses.filter(
                (dose) => dose.resultId === result.id
              );
              return { ...result, doses: resultDoses };
            });

            // Calculate nextAppointment (earliest future appointment)
            let nextAppointment = null;
            if (patientAppointments.length > 0) {
              const futureAppointments = patientAppointments
                .map((apt) => {
                  const dateStr = String(apt.testDate).slice(0, 10);
                  const [year, month, day] = dateStr.split('-').map(Number);
                  return new Date(year, month - 1, day);
                })
                .filter((date) => date >= today)
                .sort((a, b) => a - b);

              if (futureAppointments.length > 0) {
                // Local formatting; toISOString() would shift the day in UTC+ zones.
                const d = futureAppointments[0];
                nextAppointment = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
              }
            }

            // Calculate lastResult and lastResultDate
            let lastResult = null;
            let lastResultDate = null;
            if (patientResults.length > 0) {
              const sortedResults = [...patientResults].sort(
                (a, b) => {
                  const dateA = String(a.cycleStart).slice(0, 10);
                  const dateB = String(b.cycleStart).slice(0, 10);
                  return dateB.localeCompare(dateA); // Descending
                }
              );
              const latestResult = sortedResults[0];
              lastResult = latestResult?.resultValue || null;
              lastResultDate = latestResult?.cycleStart || null;
            }

            // Extract drug target values from first drug target
            const drugTarget = drugTargets[0];

            // Return enriched patient (convert isActive to proper boolean)
            const convertedIsActive = patient.isActive === undefined || patient.isActive === null ? true : Boolean(patient.isActive);
            return {
              ...patient,
              isActive: convertedIsActive,
              resultsWithDoses,
              nextAppointment,
              lastResult,
              lastResultDate,
              drugTargets,
              targetMin: drugTarget?.targetMin,
              targetMax: drugTarget?.targetMax,
            };
          } catch (error) {
            console.error(`Error enriching patient ${patient.id}:`, error);
            // Return patient with minimal enrichment on error
            return {
              ...patient,
              isActive: patient.isActive === undefined || patient.isActive === null ? true : Boolean(patient.isActive),
              resultsWithDoses: [],
              nextAppointment: null,
              lastResult: null,
              lastResultDate: null,
              drugTargets: [],
              targetMin: undefined,
              targetMax: undefined,
            };
          }
        })
      );

      // If a newer fetch superseded this one while we were enriching, discard
      // our results so we don't clobber the fresher state (last-write-wins
      // protection).
      if (isStale()) {
        console.log('⏭️ fetchPatients: Stale response discarded (superseded by newer fetch)');
        return patients.value;
      }

      // Step 4: Update store with enriched data - build locally, assign once.
      patients.value = enrichedPatients;
      appointments.value = allAppointments; // Update global appointments from collected data
      doses.value = allDoses; // Commit recurrent doses in a single assignment
      lastFetched.value = Date.now();
      isEnriched.value = true;

      // Auto-select first active patient if current selection is invalid
      const currentSelectedId = activeSelectedPatientId.value;
      const selectedPatientExists = currentSelectedId && activePatients.value.some(p => p.id === currentSelectedId);
      
      if (!selectedPatientExists) {
        const firstActive = activePatients.value[0];
        if (firstActive) {
          // Go through the reactive setters (not raw localStorage) so selectedPatient
          // / activeSelectedPatient recompute immediately — a direct localStorage
          // write here left the reactive refs stale and desynced the views.
          persistSelectedId(ACTIVE_SELECTED_PATIENT_KEY, activeSelectedPatientIdRef, firstActive.id);
          persistSelectedId(SELECTED_PATIENT_KEY, selectedPatientIdRef, firstActive.id);
        }
      }
      
      return patients.value;
    } catch (error) {
      console.error('Error fetching patients:', error);
      throw error;
    } finally {
      // Only clear the loading flag if we are still the newest request; a
      // stale request finishing must not hide the spinner of a fresher fetch.
      if (!isStale()) {
        isLoading.value = false;
      }
    }
  };

  /**
   * Fetch all appointments from the backend
   * @param {boolean} forceRefresh - Force refresh even if cache is valid
   */
  const fetchAppointments = async (forceRefresh = false) => {
    
    if (!forceRefresh && isCacheValid() && appointments.value.length > 0) {
      return appointments.value;
    }

    console.trace('Called from:');
    isLoading.value = true;
    
    try {
      const response = await dataService.getAll({ 
        selectTable: 'test_schedules',
        id: 'patientsDetails'
      });
      
      console.log('Appointments response:', {
        hasData: !!response?.data,
        length: response?.data?.length || 0,
        sample: response?.data?.slice(0, 2)
      });
      
      if (response?.data) {
        // Transform testDate to appointmentDate for calendar compatibility
        appointments.value = response.data.map(apt => {
          let appointmentDate = null;
          
          if (apt.testDate) {
            if (typeof apt.testDate === 'string') {
              // Direct string - just take the date part (YYYY-MM-DD)
              appointmentDate = apt.testDate.slice(0, 10);
            } else if (apt.testDate instanceof Date) {
              // Date object - format using local date components (not UTC/ISO)
              const year = apt.testDate.getFullYear();
              const month = String(apt.testDate.getMonth() + 1).padStart(2, '0');
              const day = String(apt.testDate.getDate()).padStart(2, '0');
              appointmentDate = `${year}-${month}-${day}`;
            } else {
              // Fallback for other formats - convert to string first
              appointmentDate = String(apt.testDate).slice(0, 10);
            }
          }
          
          return {
            ...apt,
            appointmentDate
          };
        });
      } else {
        console.warn('No appointments data in response');
      }
      
      return appointments.value;
    } catch (error) {
      console.error('Error fetching appointments:', error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch all results from the backend
   * @param {boolean} forceRefresh - Force refresh even if cache is valid
   */
  const fetchResults = async (forceRefresh = false) => {
    if (!forceRefresh && isCacheValid() && results.value.length > 0) {
      return results.value;
    }

    isLoading.value = true;
    
    try {
      const response = await dataService.getAll({ 
        selectTable: 'results',
        id: 'results'
      });
      
      if (response?.data) {
        // Sort results by cycleStart date (newest first) - do this once here instead of in every component
        results.value = response.data.sort((a, b) => {
          const dateA = String(a.cycleStart || a.resultDate).slice(0, 10);
          const dateB = String(b.cycleStart || b.resultDate).slice(0, 10);
          return dateB.localeCompare(dateA); // Descending order (newest first)
        });
      }
      
      return results.value;
    } catch (error) {
      console.error('Error fetching results:', error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch all drugs from the backend
   * @param {boolean} forceRefresh - Force refresh even if cache is valid
   */
  const fetchDrugs = async (forceRefresh = false) => {
    if (!forceRefresh && drugs.value.length > 0) {
      return drugs.value;
    }

    isLoading.value = true;
    
    try {
      const response = await dataService.getAll({ 
        selectTable: 'drugs'
      });
      
      if (response?.data) {
        drugs.value = response.data;
      }
      
      return drugs.value;
    } catch (error) {
      console.error('Error fetching drugs:', error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch notes for a specific patient
   * @param {number} patientId - The patient ID to fetch notes for
   * @param {boolean} forceRefresh - Force refresh even if cache is valid
   */
  const fetchNotesForPatient = async (patientId, forceRefresh = false) => {
    const numericPatientId = parseInt(patientId);

    // Keyed on "has this patient been loaded", NOT on "are there any notes".
    // A patient with genuinely zero notes must still count as loaded, otherwise
    // every NoteButton re-triggers a fetch and the request storm just moves from
    // /notes/find to /notes.
    if (!forceRefresh && notesLoadedPatientIds.has(numericPatientId)) {
      return notes.value.filter(n => parseInt(n.patientId) === numericPatientId);
    }

    // A calendar month renders one NoteButton per day cell, so ~30 of them can
    // mount in the same tick and each would otherwise fire its own request
    // before any of them populated the cache. De-duplicate concurrent fetches
    // for the same patient onto a single in-flight promise.
    if (!forceRefresh && notesFetchesInFlight.has(numericPatientId)) {
      return notesFetchesInFlight.get(numericPatientId);
    }

    const request = (async () => {
      try {
        const response = await dataService.getAllNotes(numericPatientId);

        if (response?.data) {
          // Remove old notes for this patient and add new ones
          notes.value = notes.value.filter(n => parseInt(n.patientId) !== numericPatientId);
          notes.value.push(...response.data);
          // Remember that this patient has been loaded even when the result is
          // empty, so callers can tell "no notes" apart from "not fetched yet".
          notesLoadedPatientIds.add(numericPatientId);
          return response.data;
        }

        notesLoadedPatientIds.add(numericPatientId);
        return [];
      } catch (error) {
        console.error('📝 Error fetching notes:', error);
        return [];
      } finally {
        notesFetchesInFlight.delete(numericPatientId);
      }
    })();

    notesFetchesInFlight.set(numericPatientId, request);
    return request;
  };

  /**
   * Whether this patient's notes have been loaded (even if they have none).
   * Lets callers resolve a single note from the cache instead of issuing a
   * per-element /notes/find request.
   */
  const areNotesLoadedForPatient = (patientId) =>
    notesLoadedPatientIds.has(parseInt(patientId));

  /**
   * Resolve one note from the already-loaded cache.
   * elementId is compared as a string: it is a date ('2026-07-25') for calendar
   * cells but a numeric row id for results/doses, and the two arrive from the
   * API with inconsistent types.
   */
  const findNoteInCache = (patientId, elementType, elementId) => {
    const numericPatientId = parseInt(patientId);
    return notes.value.find(n =>
      parseInt(n.patientId) === numericPatientId &&
      n.elementType === elementType &&
      String(n.elementId) === String(elementId)
    ) || null;
  };

  /**
   * Fetch single doses for a specific patient
   * @param {number} patientId - The patient ID to fetch single doses for
   * @param {boolean} forceRefresh - Force refresh even if cache is valid
   */
  const fetchSingleDosesForPatient = async (patientId, forceRefresh = false) => {
    const numericPatientId = parseInt(patientId);
    
    const cachedSingleDoses = singleDoses.value.filter(sd => parseInt(sd.patientId) === numericPatientId);
    if (!forceRefresh && cachedSingleDoses.length > 0) {
      return cachedSingleDoses;
    }

    try {
      const response = await dataService.getAll({
        selectTable: 'single_doses',
        id: 'single_doses',
        where: { patientId: numericPatientId }
      });
      
      if (response?.data) {
        // Remove old single doses for this patient and add new ones to singleDoses
        singleDoses.value = singleDoses.value.filter(sd => parseInt(sd.patientId) !== numericPatientId);
        singleDoses.value.push(...response.data);
        
        // Also sync to doses (with type: 'single' for backward compatibility)
        // Remove old single doses for this patient from doses array
        doses.value = doses.value.filter(d => 
          !(d.type === 'single' && parseInt(d.patientId) === numericPatientId)
        );
        // Add new single doses with type field
        const singleDosesWithType = response.data.map(d => ({ ...d, type: 'single' }));
        doses.value.push(...singleDosesWithType);
        
        return response.data;
      }
      
      return [];
    } catch (error) {
      console.error('💊 Error fetching single doses:', error);
      return [];
    }
  };

  /**
   * Enrich patients with additional data (appointments, results with doses, drug targets)
   * @deprecated Use fetchPatients instead - enrichment is now automatic
   * @param {boolean} forceRefresh - Force refresh even if cache is valid
   */
  const enrichPatients = async (forceRefresh = false) => {
    // This is now just an alias to fetchPatients for backward compatibility
    return fetchPatients(forceRefresh);
  };


  /**
   * Fetch all data (patients, appointments, results, drugs)
   * @param {boolean} forceRefresh - Force refresh even if cache is valid
   */
  const fetchAllData = async (forceRefresh = false) => {
    isLoading.value = true;
    
    try {
      await Promise.all([
        fetchPatients(forceRefresh),
        fetchAppointments(forceRefresh),
        fetchResults(forceRefresh),
        fetchDrugs(forceRefresh),
      ]);
      
    } catch (error) {
      console.error('Error fetching all data:', error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Add or update a patient in the store
   */
  const upsertPatient = (patientData) => {
    const index = patients.value.findIndex(p => p.id === patientData.id);
    
    if (index !== -1) {
      // Update existing
      patients.value[index] = { ...patients.value[index], ...patientData };
    } else {
      // Add new
      patients.value.push(patientData);
    }
    
    // Force reactivity update
    triggerRef(patients);
  };

  /**
   * Remove a patient from the store
   */
  const removePatient = (patientId) => {
    const index = patients.value.findIndex(p => p.id === patientId);
    if (index !== -1) {
      patients.value.splice(index, 1);
    }
  };

  /**
   * Add or update an appointment in the store
   */
  const upsertAppointment = (appointmentData) => {
    
    // Transform testDate to appointmentDate if needed (for calendar compatibility)
    const transformedData = { ...appointmentData };
    
    if (transformedData.testDate && !transformedData.appointmentDate) {
      // Extract just the date part (YYYY-MM-DD) from testDate
      const dateStr = transformedData.testDate.split(' ')[0]; // Handle 'YYYY-MM-DD HH:MM:SS'
      transformedData.appointmentDate = dateStr;
    }
    
    const index = appointments.value.findIndex(a => a.id === transformedData.id);
    
    if (index !== -1) {
      // Update existing
      appointments.value[index] = { ...appointments.value[index], ...transformedData };
    } else {
      // Add new
      appointments.value.push(transformedData);
    }
    
    
    // Update cache timestamp to keep cache valid after local update
    // This prevents fetchPatients from refetching and overwriting our changes
    if (!lastFetched.value) {
      lastFetched.value = Date.now();
    }
    
    // Force reactivity update
    triggerRef(appointments);
  };

  /**
   * Remove an appointment from the store
   */
  const removeAppointment = (appointmentId) => {
    const index = appointments.value.findIndex(a => a.id === appointmentId);
    if (index !== -1) {
      appointments.value.splice(index, 1);
    }
  };

  /**
   * Add or update a result in the store
   */
  const upsertResult = (resultData) => {
    
    const index = results.value.findIndex(r => r.id === resultData.id);
    
    if (index !== -1) {
      // Update existing
      results.value[index] = { ...results.value[index], ...resultData };
    } else {
      // Add new
      results.value.push(resultData);
    }
    
    // Re-enrich the patient to update resultsWithDoses (needed for pending status calculation)
    if (resultData.patientId) {
      reEnrichPatient(resultData.patientId);
    }
  };

  /**
   * Remove a result from the store
   */
  const removeResult = (resultId, patientId) => {
    const index = results.value.findIndex(r => r.id === resultId);
    if (index !== -1) {
      // Capture patientId before removing if not provided
      const pid = patientId || results.value[index]?.patientId;
      results.value.splice(index, 1);
      
      // Re-enrich the patient to update resultsWithDoses
      if (pid) {
        reEnrichPatient(pid);
      }
    }
  };

  /**
   * Add or update a dose in the store
   */
  const upsertDose = (doseData) => {
    const index = doses.value.findIndex(d => d.id === doseData.id);
    
    if (index !== -1) {
      // Update existing
      doses.value[index] = { ...doses.value[index], ...doseData };
    } else {
      // Add new
      doses.value.push(doseData);
    }
    
    // Trigger reactivity
    triggerRef(doses);
    
    // Re-enrich the patient to update resultsWithDoses
    if (doseData.patientId) {
      reEnrichPatient(doseData.patientId);
    }
  };

  /**
   * Remove a dose from the store
   */
  const removeDose = (doseId, patientId) => {
    const index = doses.value.findIndex(d => d.id === doseId);
    if (index !== -1) {
      // Capture patientId before removing if not provided
      const pid = patientId || doses.value[index]?.patientId;
      doses.value.splice(index, 1);
      // Trigger reactivity
      triggerRef(doses);
      
      // Re-enrich the patient to update resultsWithDoses
      if (pid) {
        reEnrichPatient(pid);
      }
    }
  };

  /**
   * Remove all doses for a specific result
   */
  const removeDosesByResultId = (resultId) => {
    doses.value = doses.value.filter(d => d.resultId !== resultId);
  };

  /**
   * Clear all cached data
   */
  const clearCache = () => {
    // Reset ALL cached reactive state. Omitting any per-patient clinical cache
    // here causes cross-account data bleed on user switch / impersonation, so
    // every ref the store holds must be cleared.
    patients.value = [];
    appointments.value = [];
    results.value = [];
    doses.value = [];
    drugs.value = [];
    notes.value = [];
    notesLoadedPatientIds.clear();
    notesFetchesInFlight.clear();
    singleDoses.value = [];

    // Reset cache/enrichment bookkeeping
    lastFetched.value = null;
    lastUserId.value = null; // Also clear the tracked user ID
    isEnriched.value = false;
    isCreatingNewPatient.value = false;

    // Invalidate any in-flight fetch: bump the token so a request started
    // under the previous account can never commit its data, and drop the
    // shared in-flight handle.
    fetchToken++;
    inFlightFetch = null;

    // Clear selected-patient references (ref + localStorage) so the next account
    // doesn't inherit the previous account's patient selection.
    persistSelectedId(SELECTED_PATIENT_KEY, selectedPatientIdRef, null);
    persistSelectedId(ACTIVE_SELECTED_PATIENT_KEY, activeSelectedPatientIdRef, null);
  };

  /**
   * Get patient by ID
   */
  const getPatientById = (patientId) => {
    return livePatients.value.find(p => p.id === patientId) || null;
  };

  /**
   * Get appointments for a specific patient
   */
  const getAppointmentsByPatientId = (patientId) => {
    return appointments.value.filter(a => a.patientId === patientId);
  };

  /**
   * Get results for a specific patient
   */
  const getResultsByPatientId = (patientId) => {
    return results.value.filter(r => r.patientId === patientId);
  };

  /**
   * Get doses for a specific patient
   */
  const getDosesByPatientId = (patientId) => {
    return doses.value.filter(d => d.patientId === patientId);
  };

  /**
   * Get doses for a specific result
   */
  const getDosesByResultId = (resultId) => {
    return doses.value.filter(d => d.resultId === resultId);
  };

  /**
   * Get notes for a specific patient
   */
  const getNotesByPatientId = (patientId) => {
    const numericPatientId = parseInt(patientId);
    return notes.value.filter(n => parseInt(n.patientId) === numericPatientId);
  };

  /**
   * Get single doses for a specific patient
   */
  const getSingleDosesByPatientId = (patientId) => {
    const numericPatientId = parseInt(patientId);
    return singleDoses.value.filter(sd => parseInt(sd.patientId) === numericPatientId);
  };
  /**
   * Re-enrich a single patient using existing store data (no server fetch)
   * Call this after upserting appointments/results/doses to update computed properties
   */
  const reEnrichPatient = (patientId) => {
    const numericPatientId = parseInt(patientId);
    console.log('🔄 reEnrichPatient called for patientId:', numericPatientId);
    
    const patientIndex = patients.value.findIndex(p => parseInt(p.id) === numericPatientId);
    if (patientIndex === -1) {
      console.warn('⚠️ Patient not found in store:', patientId, 'patients count:', patients.value.length);
      return;
    }

    const patient = patients.value[patientIndex];
    console.log('📋 Found patient:', patient.name || patient.id);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Get patient's appointments
    const patientAppointments = appointments.value.filter(a => parseInt(a.patientId) === numericPatientId);

    // Calculate nextAppointment(earliest future appointment AFTER today)
    // If patient has appointment today, we want to find the NEXT one after today
    let nextAppointment = null;
    if (patientAppointments.length > 0) {
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const futureAppointments = patientAppointments
        .filter(apt => {
          const aptDate = apt.appointmentDate || apt.testDate;
          if (!aptDate) return false;
          const aptDateStr = aptDate.slice(0, 10);
          return aptDateStr > todayStr; // Changed from >= to > (strictly after today)
        })
        .sort((a, b) => {
          const dateA = (a.appointmentDate || a.testDate).slice(0, 10);
          const dateB = (b.appointmentDate || b.testDate).slice(0, 10);
          return dateA.localeCompare(dateB);
        });

      if (futureAppointments.length > 0) {
        nextAppointment = (futureAppointments[0].appointmentDate || futureAppointments[0].testDate).slice(0, 10);
      }
    }

    // Get patient's results
    const patientResults = results.value.filter(r => parseInt(r.patientId) === numericPatientId);
    console.log('📊 Patient results count:', patientResults.length);
    
    // Get doses from store for this patient
    const patientDoses = doses.value.filter(d => parseInt(d.patientId) === numericPatientId);
    
    // Create resultsWithDoses by combining results with their doses
    const resultsWithDoses = patientResults.map((result) => {
      const resultDoses = patientDoses.filter((dose) => dose.resultId === result.id);
      return { ...result, doses: resultDoses };
    });
    console.log('📦 resultsWithDoses count:', resultsWithDoses.length);
    
    // Calculate lastResult and lastResultDate
    let lastResult = null;
    let lastResultDate = null;
    if (patientResults.length > 0) {
      const sortedResults = [...patientResults].sort(
        (a, b) => {
          const dateA = String(a.cycleStart || a.resultDate).slice(0, 10);
          const dateB = String(b.cycleStart || b.resultDate).slice(0, 10);
          return dateB.localeCompare(dateA);
        }
      );
      const latestResult = sortedResults[0];
      lastResult = latestResult?.resultValue || latestResult?.INR || null;
      lastResultDate = latestResult?.cycleStart || latestResult?.resultDate || null;
      console.log('📅 lastResultDate:', lastResultDate);
    }

    // Update patient with new enriched data - replace entire array to ensure Vue reactivity triggers
    const updatedPatient = {
      ...patient,
      resultsWithDoses,
      nextAppointment,
      lastResult,
      lastResultDate,
    };
    
    // Create new array to ensure reactivity propagates to all computed properties
    const newPatients = [...patients.value];
    newPatients[patientIndex] = updatedPatient;
    patients.value = newPatients;
    
    console.log('✅ Patient updated with', resultsWithDoses.length, 'results, patients array replaced');
  };

  return {
    // State
    patients: livePatients,
    activePatients,
    appointments,
    results,
    doses,
    drugs,
    notes,
    singleDoses,
    isLoading,
    isEnriched,
    lastFetched,
    isCreatingNewPatient,
    
    // Computed
    drugsById,
    selectedPatientId,
    selectedPatient,
    activeSelectedPatientId,
    activeSelectedPatient,
    
    // Methods - Fetching
    fetchPatients,
    fetchAppointments,
    fetchResults,
    fetchDrugs,
    fetchNotesForPatient,
    areNotesLoadedForPatient,
    findNoteInCache,
    fetchSingleDosesForPatient,
    fetchAllData,
    enrichPatients, // Deprecated alias to fetchPatients
    isCacheValid,
    syncActiveSelectedPatient,
    
    // Methods - CRUD
    upsertPatient,
    removePatient,
    upsertAppointment,
    removeAppointment,
    upsertResult,
    removeResult,
    upsertDose,
    removeDose,
    removeDosesByResultId,
    
    // Methods - Helpers
    clearCache,
    getPatientById,
    getAppointmentsByPatientId,
    getResultsByPatientId,
    getDosesByPatientId,
    getDosesByResultId,
    getNotesByPatientId,
    getSingleDosesByPatientId,
    reEnrichPatient,
  };
}
